import { AlertTriangle, Gauge, type LucideIcon, Radio, Thermometer } from "lucide-react";
import { type ReactNode, useMemo } from "react";

import type { TelemetryAggregates, TelemetryPayload } from "@busright/data-contract";
import { COLORS, EChart, type EChartOption, sequentialColors } from "@busright/ui";

import { ChartCard } from "@/components/ChartCard";
import { Drillable } from "@/components/Drillable";
import { EmptyState } from "@/components/EmptyState";
import {
  InsightCallout,
  type InsightCalloutProps,
  type InsightSeverity,
} from "@/components/InsightCallout";
import { MetricLabel } from "@/components/MetricTooltip";
import { NotesList } from "@/components/NotesList";
import { PageHeader } from "@/components/PageHeader";
import { TelBatteryVoltageInsight } from "@/components/TelBatteryVoltageInsight";
import { TelFreshnessMonitor } from "@/components/TelFreshnessMonitor";
import { TelMemoryFact } from "@/components/TelMemoryFact";
import { TelOutliers } from "@/components/TelOutliers";
import { useScope } from "@/context/ScopeContext";
import { useMemory, useRecipientBundle, useTelemetry } from "@/hooks/queries";
import { formatInt } from "@/lib/format";
import { scopeDisplayName } from "@/lib/scope";

// Live Telemetry — reworked into a STORY, not a fleet dump. Leads with the combined-source
// insight (battery voltage → districts with underperforming charging hardware), states the
// used/total memory FACT, and demotes freshness to a small monitor. Scope-aware: picking a
// district in the top-bar switcher re-scopes the voltage/memory read via the recipient bundle.
// Outlier rows link to /device/:serial — no bare-serial headline anywhere on this page.

type RagLevel = "ok" | "warn" | "crit";

interface MetricSpec {
  readonly key: keyof TelemetryAggregates;
  readonly label: string;
  readonly icon: LucideIcon;
  readonly unit: string;
  readonly digits: number;
  readonly level: (v: number) => RagLevel;
}

const CPU_TEMP_WARN = 45;
const CPU_TEMP_CRIT = 55;
const CPU_USAGE_WARN = 50;
const CPU_USAGE_CRIT = 75;

const highIsWorse =
  (warn: number, crit: number) =>
  (v: number): RagLevel => {
    if (v >= crit) {
      return "crit";
    }
    return v >= warn ? "warn" : "ok";
  };

// CPU stays as a small secondary metric strip — battery/memory now carry the lead story above.
const SECONDARY_METRICS: readonly MetricSpec[] = [
  {
    key: "avgCpuTempMaxC",
    label: "CPU temp (max)",
    icon: Thermometer,
    unit: "°C",
    digits: 1,
    level: highIsWorse(CPU_TEMP_WARN, CPU_TEMP_CRIT),
  },
  {
    key: "avgCpuUsagePct",
    label: "CPU usage",
    icon: Gauge,
    unit: "%",
    digits: 1,
    level: highIsWorse(CPU_USAGE_WARN, CPU_USAGE_CRIT),
  },
];

type TelemetryInsight = Pick<InsightCalloutProps, "headline" | "stats" | "severity">;

// Voltage band below which we call out a district's charging hardware as worth investigating.
const VOLT_WATCH = 3.8;
const VOLT_DIGITS = 3;
const VOLT_DISPLAY_DIGITS = 2;
const TEMP_DIGITS = 1;

// The headline sentence, split out of buildInsight so the scoped/unscoped branches read as plain
// if/else rather than a nested ternary.
const buildInsightHeadline = (
  scopeLabel: string,
  scopedVoltage: number | null,
  lowest: TelemetryPayload["outliers"]["lowestBatteryVoltage"][number] | null,
): string => {
  if (scopedVoltage !== null) {
    if (scopedVoltage < VOLT_WATCH) {
      return `${scopeLabel} is running low average charging voltage (${scopedVoltage.toFixed(VOLT_DISPLAY_DIGITS)}V) — worth a charging-hardware investigation.`;
    }
    return `${scopeLabel}'s charging voltage (${scopedVoltage.toFixed(VOLT_DISPLAY_DIGITS)}V) looks healthy.`;
  }
  if (lowest) {
    return `Lowest battery voltage fleet-wide is ${lowest.value.toFixed(VOLT_DIGITS)}V on ${lowest.hwModel ?? "an unlabeled model"} — pick a district to see whether its charging hardware is systematically underperforming.`;
  }
  return "No voltage outliers reported yet.";
};

// "Combined-source insight" — leads with the battery-voltage → charging-hardware signal (John's
// priority), backed by the real lowest-voltage device tail, with the memory fact as a second beat.
const buildInsight = (
  payload: TelemetryPayload,
  scopeLabel: string,
  scopedVoltage: number | null,
): TelemetryInsight => {
  const lowest = payload.outliers.lowestBatteryVoltage[0] ?? null;
  const isWatch = scopedVoltage !== null && scopedVoltage < VOLT_WATCH;
  const severity: InsightSeverity = isWatch ? "warn" : "info";

  return {
    headline: buildInsightHeadline(scopeLabel, scopedVoltage, lowest),
    stats: [
      { label: "Lowest voltage", value: lowest ? `${lowest.value.toFixed(VOLT_DIGITS)}V` : "—" },
      {
        label: "Devices reporting",
        value: formatInt(payload.aggregates.devicesReporting),
      },
      { label: "Scope", value: scopeLabel },
    ],
    severity,
  };
};

// Bar corner radius for the horizontal network-mix chart: rounded on the right (leading) edge only.
const NETWORK_BAR_RADIUS: readonly [number, number, number, number] = [0, 4, 4, 0];
const NETWORK_GRID = { left: 90, right: 20, top: 8, bottom: 20 } as const;

// Pulled out of the component (was an inline useMemo body) so TelemetryPage's own cognitive
// complexity stays under the lint budget — this is a pure chart-option builder with no page state.
const buildNetworkMixOption = (mix: TelemetryPayload["networkMix"]): EChartOption => {
  const colors = sequentialColors(Math.max(1, mix.length));
  return {
    grid: NETWORK_GRID,
    tooltip: {
      trigger: "axis",
      backgroundColor: COLORS.surface2,
      borderColor: COLORS.border,
      textStyle: { color: COLORS.textPrimary },
    },
    xAxis: {
      type: "value",
      splitLine: { lineStyle: { color: COLORS.border } },
      axisLabel: { color: COLORS.textMuted, fontSize: 10 },
    },
    yAxis: {
      type: "category",
      data: mix.map((m) => m.networkType),
      axisLine: { lineStyle: { color: COLORS.border } },
      axisLabel: { color: COLORS.textSecondary, fontSize: 11 },
    },
    series: [
      {
        type: "bar",
        data: mix.map((m, i) => ({
          value: m.count,
          itemStyle: { color: colors[i], borderRadius: NETWORK_BAR_RADIUS },
        })),
        barWidth: "55%",
      },
    ],
  };
};

export const TelemetryPage = (): ReactNode => {
  const { data, isLoading, isError, error } = useTelemetry();
  const memoryQuery = useMemory();
  const { scope } = useScope();
  const isScoped = scope.level !== "fleet";
  const bundleQuery = useRecipientBundle(scope);

  const meta = data?.meta;
  const payload = data?.data;
  const agg = payload?.aggregates;

  const scopedTelemetry = isScoped ? (bundleQuery.data?.data.telemetry ?? null) : null;
  const scopedVoltage = scopedTelemetry?.avgBatteryVoltageV ?? null;

  const memoryAggregate = isScoped
    ? (memoryQuery.data?.data.byDistrict.find((d) => d.scopeId === scope.id) ?? null)
    : (memoryQuery.data?.data.fleet ?? null);

  const insight = payload ? buildInsight(payload, scopeDisplayName(scope), scopedVoltage) : null;

  const networkOption = useMemo<EChartOption>(
    () => buildNetworkMixOption(payload?.networkMix ?? []),
    [payload],
  );

  if (isError) {
    return (
      <>
        <PageHeader title="Live Telemetry" meta={meta} />
        <ChartCard title="Telemetry">
          <EmptyState
            title="Couldn't load telemetry"
            message={error instanceof Error ? error.message : "Unknown error"}
            icon={AlertTriangle}
          />
        </ChartCard>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Live Telemetry"
        description={`Combined-source insight for ${scopeDisplayName(scope)} — battery voltage → charging hardware, the memory fact, and where the outlier devices actually are.`}
        meta={meta}
      />

      {insight ? (
        <InsightCallout
          headline={insight.headline}
          stats={insight.stats}
          severity={insight.severity}
          icon={Radio}
          cta={{ label: "Open Fleet Health", to: "/health" }}
        />
      ) : null}

      {payload ? (
        <div style={{ marginBottom: 16 }}>
          <TelFreshnessMonitor staleness={payload.staleness} />
        </div>
      ) : null}

      <div className="grid cols-3">
        <ChartCard
          title="Battery voltage → charging hardware"
          subtitle={
            isScoped
              ? "This scope's avg charging voltage vs the healthy band, plus the fleet-wide low-voltage device tail"
              : "Fleet-wide lowest-voltage devices — pick a district to see its charging-hardware read"
          }
          source="esper_device_status.battery_voltage_v"
          loading={isLoading || (isScoped && bundleQuery.isLoading)}
          className="span-2"
        >
          {payload ? (
            <TelBatteryVoltageInsight
              scope={scope}
              lowestVoltageOutliers={payload.outliers.lowestBatteryVoltage}
              scopedTelemetry={scopedTelemetry}
            />
          ) : (
            <EmptyState title="No voltage data" />
          )}
        </ChartCard>

        <ChartCard
          title="Memory — the fact"
          subtitle="Real used/total/free MB for this scope, not a 0–100 score"
          source="esper_device_status / esper_device_snapshot"
          loading={memoryQuery.isLoading}
        >
          <TelMemoryFact aggregate={memoryAggregate} scope={scope} />
        </ChartCard>
      </div>

      <div className="grid cols-3" style={{ marginTop: 16 }}>
        <ChartCard
          title="CPU"
          subtitle={
            agg
              ? `${formatInt(agg.devicesReporting)} devices reporting · fleet averages, RAG-thresholded`
              : undefined
          }
          source="esper_device_status"
          loading={isLoading}
        >
          {agg ? (
            <div className="metric-strip">
              {SECONDARY_METRICS.map((m) => {
                const raw = agg[m.key];
                const value = typeof raw === "number" ? raw : null;
                const level = value === null ? "warn" : m.level(value);
                return (
                  <div key={m.key} className="metric-chip">
                    <span className="mc-label">
                      <m.icon size={13} aria-hidden /> {m.label}
                    </span>
                    <span className={`mc-value ${level} br-tabular-nums`}>
                      {value === null ? "—" : value.toFixed(m.digits)}
                      <span style={{ fontSize: 12, color: COLORS.textMuted, marginLeft: 3 }}>
                        {m.unit}
                      </span>
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <EmptyState title="No telemetry aggregates" />
          )}
        </ChartCard>

        <ChartCard
          title="Cellular signal"
          subtitle="Fleet average"
          source="esper_device_status.cellular_signal_strength_db"
          loading={isLoading}
        >
          {agg ? (
            <Drillable
              metricKey="signal.cellularDb"
              scope={scope}
              label="Drill into cellular signal"
            >
              <div className="metric-chip">
                <span className="mc-label">
                  <MetricLabel metricKey="signal.cellularDb" label="Avg signal" />
                </span>
                <span className="mc-value br-tabular-nums">
                  {agg.avgCellularSignalStrengthDb === null
                    ? "—"
                    : `${formatInt(agg.avgCellularSignalStrengthDb)} dBm`}
                </span>
              </div>
            </Drillable>
          ) : (
            <EmptyState title="No signal data" />
          )}
        </ChartCard>

        <ChartCard
          title="Reporting"
          subtitle="Devices with a recent reading"
          source="esper_device_status"
          loading={isLoading}
        >
          {agg && payload ? (
            <div className="metric-strip">
              <div className="metric-chip">
                <span className="mc-label">
                  <Radio size={13} aria-hidden /> Reporting
                </span>
                <span className="mc-value br-tabular-nums">{formatInt(agg.devicesReporting)}</span>
              </div>
              <div className="metric-chip">
                <span className="mc-label">Fresh readings</span>
                <span className="mc-value ok br-tabular-nums">
                  {formatInt(payload.staleness.fresh)}
                </span>
              </div>
              <div className="metric-chip">
                <span className="mc-label">Stale readings</span>
                <span className="mc-value crit br-tabular-nums">
                  {formatInt(payload.staleness.stale)}
                </span>
              </div>
            </div>
          ) : (
            <EmptyState title="No reporting data" />
          )}
        </ChartCard>
      </div>

      <div style={{ marginTop: 16 }}>
        <ChartCard
          title="Cellular carrier / network mix"
          subtitle="Distinct devices by network type"
          source="esper_device_status.network_type"
          loading={isLoading}
        >
          {payload && payload.networkMix.length > 0 ? (
            <EChart option={networkOption} height={160} />
          ) : (
            <EmptyState title="No network data" />
          )}
        </ChartCard>
      </div>

      <div className="grid cols-3" style={{ marginTop: 16 }}>
        <ChartCard
          title="Hottest battery"
          subtitle="Per-device latest reading — click a row to open Device-360"
          source="esper_device_status.battery_temperature_c"
          loading={isLoading}
        >
          <TelOutliers
            rows={payload?.outliers.hottestBattery ?? []}
            valueHeader="Temp"
            formatValue={(v) => `${v.toFixed(TEMP_DIGITS)}°C`}
            emptyTitle="No battery outliers"
          />
        </ChartCard>

        <ChartCard
          title="Lowest battery voltage"
          subtitle="Per-device latest reading — click a row to open Device-360"
          source="esper_device_status.battery_voltage_v"
          loading={isLoading}
        >
          <TelOutliers
            rows={payload?.outliers.lowestBatteryVoltage ?? []}
            valueHeader="Voltage"
            formatValue={(v) => `${v.toFixed(VOLT_DIGITS)}V`}
            emptyTitle="No voltage outliers"
          />
        </ChartCard>

        <ChartCard
          title="Hottest CPU"
          subtitle="Per-device latest reading — click a row to open Device-360"
          source="esper_device_status.cpu_temp_max_c"
          loading={isLoading}
        >
          <TelOutliers
            rows={payload?.outliers.hottestCpu ?? []}
            valueHeader="Temp"
            formatValue={(v) => `${v.toFixed(TEMP_DIGITS)}°C`}
            emptyTitle="No CPU outliers"
          />
        </ChartCard>
      </div>

      {meta ? (
        <div style={{ marginTop: 16 }}>
          <ChartCard title="Data notes" loading={isLoading}>
            <NotesList notes={meta.notes} />
          </ChartCard>
        </div>
      ) : null}
    </>
  );
};
